<template>
  <div class="app-page matched-list-page">
    <div class="matched-content">
      <!-- 推荐用户区域 -->
      <section class="matched-section">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon-wrapper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
            <div>
              <h3 class="section-title">推荐用户</h3>
              <p class="section-desc">为你精选的潜在匹配对象</p>
            </div>
          </div>
          <button
            class="section-refresh-btn"
            :disabled="loading"
            @click="fetchRecommendUsers"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'spinning': loading }"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
            刷新
          </button>
        </div>

        <!-- 骨架加载 -->
        <div v-if="loading" class="skeleton-grid">
          <div v-for="n in 4" :key="n" class="skeleton-card">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-lines">
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-40"></div>
              <div class="skeleton-line w-80"></div>
            </div>
            <div class="skeleton-actions">
              <div class="skeleton-btn"></div>
              <div class="skeleton-btn"></div>
            </div>
          </div>
        </div>

        <div v-else-if="recommendUsers.length > 0" class="user-grid">
          <div
            v-for="user in recommendUsers"
            :key="user.userId"
            class="user-card"
          >
            <div class="user-card-bg"></div>
            <div class="user-card-top">
              <img
                :src="user.avatar || 'https://picsum.photos/id/64/200'"
                :alt="user.nickname"
                class="user-avatar"
              />
              <div class="user-match-score" v-if="user.matchScore">
                <span class="score-value">{{ user.matchScore }}%</span>
                <span class="score-label">匹配</span>
              </div>
            </div>
            <div class="user-card-body">
              <h4 class="user-nickname">{{ user.nickname }}</h4>
              <p class="user-basic">{{ user.age }}岁 · {{ user.gender === 1 ? '男' : '女' }}</p>
              <p class="user-hobby">{{ user.hobby || '暂无爱好' }}</p>
            </div>
            <div class="user-card-actions">
              <button class="action-btn action-primary" @click="matchUser(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                发起匹配
              </button>
              <button class="action-btn action-secondary" @click="viewUserDetail(user)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                详情
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-section">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          <p>暂无推荐用户</p>
          <span>快去发现更多有趣的人吧</span>
        </div>
      </section>

      <!-- 匹配记录区域 -->
      <section class="matched-section">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon-wrapper icon-purple">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
            </div>
            <div>
              <h3 class="section-title">匹配记录</h3>
              <p class="section-desc">谁曾向你表达过喜欢</p>
            </div>
          </div>
          <button
            class="section-refresh-btn"
            :disabled="loadingLikedMe"
            @click="fetchLikedMeUsers"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'spinning': loadingLikedMe }"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
            刷新
          </button>
        </div>

        <div v-if="loadingLikedMe" class="skeleton-grid">
          <div v-for="n in 4" :key="n" class="skeleton-card">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-lines">
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-40"></div>
              <div class="skeleton-line w-80"></div>
            </div>
            <div class="skeleton-actions">
              <div class="skeleton-btn"></div>
              <div class="skeleton-btn"></div>
            </div>
          </div>
        </div>

        <div v-else-if="likedMeUsers.length > 0" class="user-grid">
          <div
            v-for="user in likedMeUsers"
            :key="user.userId"
            class="user-card"
          >
            <div class="user-card-bg"></div>
            <div class="user-card-top">
              <img
                :src="user.avatar || 'https://picsum.photos/id/64/200'"
                :alt="user.nickname"
                class="user-avatar"
              />
              <div class="user-like-time">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                {{ formatTime(user.likeTime) }}
              </div>
            </div>
            <div class="user-card-body">
              <h4 class="user-nickname">{{ user.nickname }}</h4>
              <p class="user-basic">{{ user.age }}岁 · {{ user.gender === 1 ? '男' : '女' }}</p>
              <p class="user-hobby">{{ user.hobby || '暂无爱好' }}</p>
            </div>
            <div class="user-card-actions">
              <button class="action-btn action-primary" @click="matchUser(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                接受匹配
              </button>
              <button class="action-btn action-secondary" @click="viewUserDetail(user)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                详情
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-section">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
          <p>暂无匹配记录</p>
          <span>耐心等待，对的人正在靠近</span>
        </div>
      </section>

      <!-- 匹配成功区域 -->
      <section class="matched-section">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon-wrapper icon-success">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <div>
              <h3 class="section-title">匹配成功</h3>
              <p class="section-desc">双向奔赴的美好时刻</p>
            </div>
          </div>
          <button
            class="section-refresh-btn"
            :disabled="loadingMatched"
            @click="fetchMatchedUsers"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'spinning': loadingMatched }"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
            刷新
          </button>
        </div>

        <div v-if="loadingMatched" class="skeleton-grid">
          <div v-for="n in 4" :key="n" class="skeleton-card">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-lines">
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-40"></div>
              <div class="skeleton-line w-80"></div>
            </div>
            <div class="skeleton-actions">
              <div class="skeleton-btn"></div>
              <div class="skeleton-btn"></div>
            </div>
          </div>
        </div>

        <div v-else-if="matchedUsers.length > 0" class="user-grid">
          <div
            v-for="user in matchedUsers"
            :key="user.userId"
            class="user-card user-card-matched"
          >
            <div class="user-card-bg"></div>
            <div class="match-badge">匹配成功</div>
            <div class="user-card-top">
              <img
                :src="user.avatar || 'https://picsum.photos/id/64/200'"
                :alt="user.nickname"
                class="user-avatar"
              />
              <div class="user-like-time">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {{ formatTime(user.matchTime) }}
              </div>
            </div>
            <div class="user-card-body">
              <h4 class="user-nickname">{{ user.nickname }}</h4>
              <p class="user-basic">{{ user.age }}岁 · {{ user.gender === 1 ? '男' : '女' }}</p>
              <p class="user-hobby">{{ user.hobby || '暂无爱好' }}</p>
            </div>
            <div class="user-card-actions">
              <button class="action-btn action-primary" @click="startChat(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                开始聊天
              </button>
              <button class="action-btn action-secondary" @click="viewUserDetail(user)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                详情
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-section">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <p>暂无匹配成功记录</p>
          <span>勇敢表达喜欢，故事就此开始</span>
        </div>
      </section>
    </div>

    <!-- 用户资料卡片 -->
    <UserProfileCard
      :visible="showDetailDialog"
      :profile="userDetail"
      @close="showDetailDialog = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import matchApi from '@/api/match.js'
import userProfileApi from '@/api/userProfile'
import UserProfileCard from '@/views/match/UserProfileCard.vue'
import axios from 'axios'

// 响应式数据
const currentUserId = ref(localStorage.getItem('userId') || null)
const recommendUsers = ref([])
const likedMeUsers = ref([])
const matchedUsers = ref([])
const loading = ref(false)
const loadingLikedMe = ref(false)
const loadingMatched = ref(false)

// 查看详情所需变量
const userDetail = ref({})
const showDetailDialog = ref(false)
// 格式化时间
const formatTime = (time) => {
  if (!time) return ''
  const date = new Date(time)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 获取推荐用户
const fetchRecommendUsers = async () => {
  if (!currentUserId.value) return

  loading.value = true
  try {
    const res = await matchApi.getRecommendUsers(currentUserId.value)
    if (res.data && (res.data.code === 200 || res.data.success)) {
      recommendUsers.value = res.data.data?.records || res.data.records || []
    }
  } catch (error) {
    ElMessage.error('获取推荐用户失败')
  } finally {
    loading.value = false
  }
}

// 获取谁喜欢我
const fetchLikedMeUsers = async () => {
  if (!currentUserId.value) return

  loadingLikedMe.value = true
  try {
    const res = await matchApi.getLikedMe(currentUserId.value)
    if (res.data && (res.data.code === 200 || res.data.success)) {
      likedMeUsers.value = res.data.data?.records || res.data.records || []
    }
  } catch (error) {
    ElMessage.error('获取喜欢记录失败')
  } finally {
    loadingLikedMe.value = false
  }
}

// 获取匹配成功用户
const fetchMatchedUsers = async () => {
  if (!currentUserId.value) return

  loadingMatched.value = true
  try {
    const res = await matchApi.getMatchedUsers(currentUserId.value)
    if (res.data && (res.data.code === 200 || res.data.success)) {
      matchedUsers.value = res.data.data?.records || res.data.records || []
    }
  } catch (error) {
    ElMessage.error('获取匹配记录失败')
  } finally {
    loadingMatched.value = false
  }
}

// 匹配用户
const matchUser = async (targetUserId) => {
  if (!currentUserId.value) return

  try {
    const res = await matchApi.likeUser({
      userId: currentUserId.value,
      targetUserId: targetUserId
    })
    if (res.data.success) {
      ElMessage.success('已接受喜欢，匹配成功！')
      fetchLikedMeUsers()
      fetchMatchedUsers()
    }
  } catch (error) {
    ElMessage.error('操作失败，请重试')
  }
}

// 查看详情（使用统一资料卡片）
const viewUserDetail = async (user) => {
  const targetUserId = user.userId
  if (!targetUserId) return

  userDetail.value = null
  showDetailDialog.value = true

  try {
    const basicInfo = { nickname: user.nickname, avatar: user.avatar, userId: targetUserId }
    const res = await userProfileApi.getByUserId(targetUserId)
    console.log('📋 匹配资料:', res.data)
    if (res.data && (res.data.code === '200' || res.data.code === 200) && res.data.data) {
      userDetail.value = { ...basicInfo, ...res.data.data }
    } else {
      userDetail.value = basicInfo
    }
  } catch (error) {
    console.error('获取用户资料失败:', error)
    userDetail.value = { nickname: user.nickname, avatar: user.avatar }
  }
}


import { useRouter } from 'vue-router';

const router = useRouter();
// 开始聊天
const startChat = (targetUserId) => {
  console.log('开始与用户', targetUserId, '聊天');
  router.push({
    path: '/',
    query: { targetUserId }
  });
};

// 初始化数据
onMounted(() => {
  if (currentUserId.value) {
    fetchRecommendUsers()
    fetchLikedMeUsers()
    fetchMatchedUsers()
  }
})
</script>

<style scoped>
.matched-list-page {
  min-height: 100%;
}

.matched-content {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── 区块 ── */
.matched-section {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  padding: 24px;
  transition: box-shadow 0.25s ease;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border-light);
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.section-icon-wrapper {
  width: 42px;
  height: 42px;
  border-radius: var(--app-radius-md);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-icon-wrapper.icon-purple {
  background: rgba(108, 92, 231, 0.1);
  color: var(--app-color-primary, #8B7CF7);
  color: #8B7CF7;
}

.section-icon-wrapper.icon-success {
  background: rgba(46, 204, 113, 0.1);
  color: #2ecc71;
}

.section-title {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

.section-desc {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
  margin: 2px 0 0;
}

.section-refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: var(--app-bg-elevated);
  color: var(--app-text-secondary);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.section-refresh-btn:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
}

.section-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spinning {
  animation: spin 1s linear infinite;
}

/* ── 骨架屏 ── */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

.skeleton-card {
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.skeleton-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-lines {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.w-60 { width: 60%; }
.w-40 { width: 40%; }
.w-80 { width: 80%; }

.skeleton-actions {
  display: flex;
  gap: 10px;
  width: 100%;
}

.skeleton-btn {
  flex: 1;
  height: 36px;
  border-radius: 18px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ── 卡片网格 ── */
.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

/* ── 用户卡片 ── */
.user-card {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-light);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  animation: cardIn 0.4s ease both;
}

.user-card:nth-child(1) { animation-delay: 0s; }
.user-card:nth-child(2) { animation-delay: 0.05s; }
.user-card:nth-child(3) { animation-delay: 0.1s; }
.user-card:nth-child(4) { animation-delay: 0.15s; }

@keyframes cardIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.user-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--app-shadow-card-hover);
  border-color: transparent;
}

.user-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: var(--app-gradient-primary);
  opacity: 0.08;
}

.user-card-matched .user-card-bg {
  background: linear-gradient(135deg, #8B7CF7 0%, #A99BFF 100%);
  opacity: 0.1;
}

.match-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #8B7CF7, #A99BFF);
  color: white;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: var(--app-radius-full);
  z-index: 2;
}

.user-card-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 12px;
  position: relative;
}

.user-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--app-bg-elevated);
  box-shadow: 0 4px 12px rgba(255, 126, 126, 0.2);
  position: relative;
  z-index: 1;
  transition: transform 0.3s;
}

.user-card:hover .user-avatar {
  transform: scale(1.05);
}

.user-match-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -16px;
  background: linear-gradient(135deg, #FF7E7E, #FF8E53);
  color: white;
  padding: 6px 14px;
  border-radius: var(--app-radius-full);
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.3);
}

.score-value {
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.2;
}

.score-label {
  font-size: 0.625rem;
  opacity: 0.9;
}

.user-like-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.6875rem;
  color: var(--app-text-muted);
  margin-top: 6px;
}

.user-card-body {
  padding: 0 16px 12px;
  text-align: center;
}

.user-nickname {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0 0 4px;
}

.user-basic {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  margin: 0 0 4px;
}

.user-hobby {
  font-size: 0.75rem;
  color: var(--app-text-muted);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── 卡片操作按钮 ── */
.user-card-actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--app-border-light);
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: var(--app-radius-full);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  border: none;
}

.action-primary {
  background: var(--app-gradient-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.2);
}

.action-primary:hover {
  box-shadow: 0 4px 14px rgba(255, 126, 126, 0.35);
  transform: translateY(-1px);
}

.action-secondary {
  background: var(--app-bg-soft);
  color: var(--app-text-secondary);
  border: 1px solid var(--app-border-light);
}

.action-secondary:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
}

/* ── 空状态 ── */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: var(--app-text-muted);
  text-align: center;
}

.empty-section svg {
  margin-bottom: 12px;
}

.empty-section p {
  font-size: 0.9375rem;
  color: var(--app-text-secondary);
  margin: 0 0 4px;
  font-weight: 500;
}

.empty-section span {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
}

/* ── 详情弹窗 ── */
.detail-dialog :deep(.el-dialog__body) {
  padding-top: 8px;
}

.detail-content {
  text-align: center;
  padding: 8px 0;
}

.detail-avatar {
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
}

.detail-avatar img {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 1;
}

.detail-avatar-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  border: 3px solid var(--app-color-primary-soft);
  z-index: 0;
}

.detail-match-score {
  color: var(--app-color-primary);
  font-weight: 600;
}

.detail-descriptions :deep(.el-descriptions__label) {
  color: var(--app-text-secondary);
  font-weight: 500;
}

.skeleton-container {
  padding: 20px 0;
}

/* ── 响应式 ── */
@media (max-width: 768px) {
  .matched-section {
    padding: 16px;
    border-radius: var(--app-radius-md);
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .section-refresh-btn {
    align-self: flex-start;
  }

  .user-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }

  .user-avatar {
    width: 64px;
    height: 64px;
  }

  .user-card-actions {
    flex-direction: column;
  }

  .action-btn {
    padding: 6px 10px;
    font-size: 0.75rem;
  }
}

@media (max-width: 400px) {
  .user-grid {
    grid-template-columns: 1fr;
  }
}
</style>